import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, NgModel } from '@angular/forms';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepicker } from '@angular/material/datepicker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { Moment } from 'moment';
import { Observable, map, startWith } from 'rxjs';
import { DDMMYYYY_FORMAT } from 'src/app/invoices/invoice/invoice.component';
import { Consultant, DistributionAmount } from 'src/app/models/consultant.model';
import { Invoice, InvoiceStatus, InvoiceTask, Order } from 'src/app/models/order.model';
import { ConsultantService } from 'src/app/services/consultant.service';
import { InvoiceService } from 'src/app/services/invoice.service';
import { OrderService } from 'src/app/services/order.service';
import { ConfirmDialogComponent } from 'src/app/ui/confirm-dialog/confirm-dialog.component';
import { round2 } from 'src/app/helpers/math-helper';

export declare type OrderId = {
  taskNumber: string;
  orderId: string;
};

@Component({
    selector: 'app-manual-invoicing',
    templateUrl: './manual-invoicing.component.html',
    styleUrls: ['./manual-invoicing.component.scss'],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'da-DK' },
        { provide: MAT_DATE_FORMATS, useValue: DDMMYYYY_FORMAT },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ManualInvoicingComponent implements OnInit, AfterViewInit {

  @Input() invoice: Invoice;
  @Input() order: Order;
  @Input() fromOrder: boolean;
  @Input() autofocusInvoiceNumber = false;
  @ViewChildren(NgModel) ngModels: QueryList<NgModel>;
  @ViewChild('invoiceNumberInput') invoiceNumberInput: ElementRef<HTMLInputElement>;

  @Output() close = new EventEmitter<Invoice>();

  loading = true;



  formGroup: FormGroup;
  orderIds: OrderId[];
  orderControl = new FormControl();
  filteredOrders: Observable<OrderId[]>;
  dateControl = new FormControl();
  consultants: Consultant[];
  leadCreatorControl = new FormControl();
  filteredLeadCreators: Observable<Consultant[]>;
  salesRealizatorControl = new FormControl();
  executorControl = new FormControl();

  invoiceAmount: number;
  leadCreator: DistributionAmount;

  leadCreatorSplitPercent = 5;
  salesRealizatorSplitPercent = 10;
  // executorSplitPercent = 80;

  constructor(
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private orderService: OrderService,
    protected consultantService: ConsultantService,
    private invoiceService: InvoiceService
  ) {
    this.formGroup = new FormGroup({
      order: this.orderControl,
      date: this.dateControl,
      leadCreator: this.leadCreatorControl,
      salesRealizator: this.salesRealizatorControl,
      executor: this.executorControl
    });
  }


  async ngOnInit(): Promise<void> {

  }

  ngAfterViewInit(): void {
    (async () => {
      this.orderIds = await this.orderService.taskNumbers();
      this.consultants = await this.consultantService.list();

      if (this.fromOrder) {
        this.invoice.order = this.order;
      }

      if (this.invoice?.order) {
        this.order = this.invoice.order;
        setTimeout(() => {
          this.formGroup.patchValue({ date: this.invoice.date ?? moment.utc().toDate() });
        });
        this.invoiceAmount = this.invoice.tasks?.map(_ => _.amount * _.price).reduce((prev, curr) => prev + curr, 0);
        // Null on invoices whose percent the backfill migration could not recover
        // (e.g. 0 kr invoices) — for those the default is kept.
        if (this.invoice.salesRealizatorSplitPercent != null) {
          this.salesRealizatorSplitPercent = this.invoice.salesRealizatorSplitPercent;
        }
      } else if (this.order) {
        this.invoice = new Invoice();
        this.invoice.salesRealizatorDistribution = [];
        this.invoice.distribution = [];
        setTimeout(() => {
          this.formGroup.patchValue({
            order: { taskNumber: this.order.taskNumber, orderId: this.order.id },
            leadCreator: this.consultants.find(_ => _.id == this.order.leadCreatorId),
          });
        });
      } else {
        this.invoice = new Invoice();
        this.invoice.distribution = [];
        this.invoice.salesRealizatorDistribution = [];
      }

      this.filteredOrders = this.orderControl.valueChanges.pipe(
        startWith(''),
        map(value => {
          const taskNumber = typeof value === 'string' ? value : value?.taskNumber;
          return taskNumber ? this.filterOrders(taskNumber as string) : this.orderIds.slice();
        }),
      );

      this.orderControl.valueChanges.subscribe(async _ => {
        if (_ && typeof _ !== "string") {
          this.order = await this.orderService.get(_.orderId);

          // Opening an existing invoice (or a duplicate) patches its own order into the
          // control. Only take the order's values when another order is picked, so the
          // invoice keeps what was registered on it even if the order changed since.
          if (this.invoice.orderId != this.order.id) {
            const consultant = this.consultants.find(_ => _.id == this.order.consultantId);
            const leadCreator = this.consultants.find(_ => _.id == this.order.leadCreatorId);

            this.invoice.orderId = this.order.id;
            this.invoice.status = InvoiceStatus.Manual;
            this.invoice.projectLeadId = leadCreator?.id;
            this.invoice.departmentId = consultant?.departmentId;
            this.invoice.taskNumber = this.order.taskNumber;
            this.invoice.customerName = this.order.customerAndTask;
            this.invoice.economicCustomerNumber = this.order.economicCustomerNumber;

            this.leadCreatorControl.setValue(leadCreator);
          }

          if (this.autofocusInvoiceNumber) {
            this.autofocusInvoiceNumber = false;
            setTimeout(() => this.invoiceNumberInput?.nativeElement.focus());
          }
        }
      });
      if (this.invoice?.order) {
        setTimeout(() => {
          console.log(this.invoice?.salesRealizatorDistribution);
          this.formGroup.patchValue({
            order: { taskNumber: this.invoice?.taskNumber, orderId: this.invoice?.orderId },
            // ProjectLeadId is what Honorargrundlag credits, so the dialog shows the same
            leadCreator: this.consultant(this.invoice?.projectLeadId ?? this.invoice?.leadCreatorDistribution?.consultantId),
            salesRealizator: this.invoice?.salesRealizatorDistribution.map(_ => _.consultantId),
            executor: this.invoice?.distribution.map(_ => _.consultantId),
          });
        });
      }

      this.filteredLeadCreators = this.leadCreatorControl.valueChanges.pipe(
        startWith(''),
        map(value => {
          const initials = typeof value === 'string' ? value : value?.initials;
          return initials ? this.filterLeadCreators(initials as string) : this.consultants.slice();
        }),
      );

      this.leadCreatorControl.valueChanges.subscribe(_ => {
        if (_ && typeof _ !== "string") {
          this.invoice.projectLeadId = _.id;
          if (!this.invoice.leadCreatorDistribution) {
            this.invoice.leadCreatorDistribution = new DistributionAmount(
              _.id,
              this.order?.id ?? this.invoice?.orderId,
              this.invoiceAmount,
              null,
              null,
              this.invoice?.id
            );
          } else {
            this.invoice.leadCreatorDistribution.consultantId = _.id;
          }
        }
      });

      this.salesRealizatorControl.valueChanges.subscribe(salesRealizators => {
        if (this.invoice?.order && salesRealizators?.length == 0) return;

        let toRemove = this.invoice.salesRealizatorDistribution
          ?.map(_ => _.consultantId)
          ?.filter(_ => !salesRealizators?.includes(_));

        toRemove?.forEach(e => {
          let idx = this.invoice.salesRealizatorDistribution.findIndex(__ => __.consultantId == e);
          this.invoice.salesRealizatorDistribution.splice(idx, 1);
        });

        salesRealizators?.forEach(e => {
          if (!this.invoice.salesRealizatorDistribution?.some(__ => __.consultantId == e)) {
            let s = this.order.salesRealizators.find(_ => _.consultantId == e);
            this.invoice.salesRealizatorDistribution.push(new DistributionAmount(e, this.order.id, (s?.amount / this.order.projectSumExVat) * this.invoiceAmount, 0, null, this.invoice.id));
          }
        });
      });

      this.executorControl.valueChanges.subscribe(executors => {
        if (this.invoice?.order && executors?.length == 0) return;
        let toRemove = this.invoice.distribution
          ?.map(_ => _.consultantId)
          ?.filter(_ => !executors?.includes(_));

        toRemove?.forEach(e => {
          let idx = this.invoice.distribution.findIndex(__ => __.consultantId == e);
          this.invoice.distribution.splice(idx, 1);
        });

        executors?.forEach(e => {
          if (!this.invoice.distribution?.some(__ => __.consultantId == e)) {
            let s = this.order.executors.find(_ => _.consultantId == e);
            this.invoice.distribution.push(new DistributionAmount(e, this.order.id, (s?.amount / this.order.projectSumExVat) * this.invoiceAmount, 0, this.invoice.id));
          }
        });
      });

      this.loading = false;
    })();
  }

  useBusinessRuleChanged() {
    setTimeout(() => {
      const current = this.salesRealizatorControl.value;
      const isEmpty = !current || (Array.isArray(current) && current.length === 0);
      if (isEmpty) {
        this.salesRealizatorControl.setValue(this.order.salesRealizators.map(_ => _.consultantId));
      }
    });
  }

  private filterOrders(value: string): OrderId[] {
    const filterValue = value.toLowerCase();
    return this.orderIds.filter(option => option.taskNumber.toLowerCase().includes(filterValue));
  }

  orderDisplayFn(order: OrderId): string {
    return order && order.taskNumber ? order.taskNumber : '';
  }

  private filterLeadCreators(value: string): Consultant[] {
    const filterValue = value.toLowerCase();
    return this.consultants.filter(option => option.initials.toLowerCase().includes(filterValue));
  }

  dateSelected(event: MatDatepickerInputEvent<Moment>, datepicker: MatDatepicker<Date>) {
    if (datepicker) {
      datepicker.close();
    }

    this.invoice.date = event.value.add(12, "hours").toDate();
  }

  consultant(id: string) {
    return this.consultants?.find(_ => _.id == id);
  }

  async save() {
    if (!this.valid()) {
      this.formGroup.markAllAsTouched();
      this.ngModels.forEach(_ => _.control.markAllAsTouched());
    } else {
      try {
        // Require confirmation when not using business rule
        if (!this.invoice.useBusinessRule) {
          const confirmed = await this.confirmManualInvoicing();
          if (!confirmed) {
            return;
          }
        }
        if (!this.invoice.tasks || this.invoice.tasks?.length == 0) {
          this.invoice.tasks = [new InvoiceTask("Manuelt oprettet", 1, this.invoiceAmount)];
        }
        else {
          this.invoice.tasks[0].price = this.invoiceAmount;
        }
        if (this.invoice.useBusinessRule) {
          this.invoice.salesRealizatorSplitPercent = this.salesRealizatorSplitPercent;
          this.invoice.leadCreatorDistribution.feeBasis = round2(this.invoice.leadCreatorDistribution.amount * this.leadCreatorSplitPercent / 100);
          for (let d of this.invoice.salesRealizatorDistribution) {
            d.feeBasis = round2(d.amount * this.salesRealizatorSplitPercent / 100);
          }

          const executorSplitPercent = 95 /* 5% to LEAD (the company) */ - (this.leadCreatorSplitPercent + this.salesRealizatorSplitPercent);
          for (let d of this.invoice.distribution) {
            d.feeBasis = round2(d.amount * executorSplitPercent / 100);
          }
        } else {
          this.invoice.salesRealizatorDistribution = [];
          this.invoice.leadCreatorDistribution = undefined;
          this.invoice.salesRealizatorSplitPercent = null;

          for (let d of this.invoice.distribution) {
            d.feeBasis = d.amount;
          }
        }
        const result = await this.invoiceService.save(this.invoice, this.order.invoicingFinished);
        this.snackBar.open("Fakturaen er gemt");
        this.reset();
        this.close.emit(result);
      } catch (error: any) {
        console.error(error);
        if (error.status == 400 && error.error?.errors?.economicInvoiceNumber) {
          this.snackBar.open("Faktura nr. eksisterer allerede", "Luk", { panelClass: 'snackbar-error', duration: 0 });
        } else {
          this.snackBar.open("Der skete en fejl. Prøv igen senere", "Luk", { panelClass: 'snackbar-error', duration: 0 });
        }
      }
    }
  }

  private async confirmManualInvoicing(): Promise<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Bekræft bogføring',
        message: 'Du har ændret ift. retningslinjer for lead creator og salgsrealisator. Er du sikker på at du vil fortsætte?',
        confirmText: 'Ja, bogfør',
        cancelText: 'Annuller'
      }
    });
    const result = await dialogRef.afterClosed().toPromise();
    return !!result;
  }

  reset() {
    this.ngModels.forEach(_ => _.control.reset());
    this.formGroup.reset();
    this.invoice = new Invoice();
    this.order = null;
    this.invoice.salesRealizatorDistribution = [];
    this.invoice.distribution = [];
    this.salesRealizatorSplitPercent = 10;
  }

  valid() {
    if (this.invoice.useBusinessRule) {
      if (this.salesRealizatorAmountError()) {
        return false;
      }
    }
    return !this.executorAmountError() && this.formGroup.valid && this.ngModels.map(_ => _.valid).every(_ => _);
  }

  salesRealizatorAmountError() {
    if (this.invoice.salesRealizatorDistribution.some(_ => Number.isNaN(_.amount))) {
      return true;
    }
    let total = this.invoice.salesRealizatorDistribution?.reduce((prev, curr) => prev + Number(curr.amount), 0);
    let totalInvoice = this.invoiceAmount;
    const diff = Math.abs(totalInvoice - total);
    return diff > 0.01;
  }

  executorAmountError() {
    if (this.invoice.distribution.some(_ => Number.isNaN(_.amount))) {
      return true;
    }
    let total = this.invoice.distribution?.reduce((prev, curr) => prev + Number(curr.amount), 0);
    let totalInvoice = this.invoiceAmount;
    const diff = Math.abs(totalInvoice - total);
    return diff > 0.01;
  }

  updateAmount(da: DistributionAmount, percentage: number) {
    const base = this.invoiceAmount || 0;
    if (!base) return;

    const currentPercentRounded = round2((+da.amount / base) * 100);
    const nextPercentRounded = round2(+percentage);

    // If the user didn't actually change the displayed percentage, do not recompute amount
    if (currentPercentRounded === nextPercentRounded) return;

    da.amount = round2((+percentage / 100) * base);
  }

  getAmountPercent(da: DistributionAmount): number | null {
    if (!this.invoiceAmount) return null;
    return da.amount / this.invoiceAmount * 100;
  }

  // hasFeeBasisWarning(): boolean {
  //   return this.leadCreatorSplitPercent + this.salesRealizatorSplitPercent + this.executorSplitPercent > 95;
  // }

  invoiceAmountSet($event: any) {
    this.invoiceAmount = typeof $event === 'number' ? $event : Number($event);
    const currentSales = this.salesRealizatorControl.value;
    const salesEmpty = !currentSales || (Array.isArray(currentSales) && currentSales.length === 0);
    if (salesEmpty) {
      this.salesRealizatorControl.setValue(this.order.salesRealizators.map(_ => _.consultantId));
    }

    const currentExecutors = this.executorControl.value;
    const executorsEmpty = !currentExecutors || (Array.isArray(currentExecutors) && currentExecutors.length === 0);
    if (executorsEmpty) {
      this.executorControl.setValue(this.order.executors.map(_ => _.consultantId));
    }
    if (this.invoice.leadCreatorDistribution) {
      const base = this.invoiceAmount || 0;
      this.invoice.leadCreatorDistribution.amount = base;
    }
  }
}
